import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import { resolveTaxLayout } from "./taxLayouts";

// Format currency for display on form
const formatCurrency = (amount) => {
  const num = parseFloat(amount) || 0;
  if (num === 0) return "";
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Get template URL based on tax year
export const getTemplateUrl = (taxYear) => {
  const year = parseInt(taxYear);
  return `/templates/1099nec-${year}.pdf`;
};

// Field positions for 1099-NEC form (Copy 1 for State - page index 2)
// Page height: 792, coordinates from bottom
export const DEFAULT_FIELD_POSITIONS = {
  payerName: { x: 60, y: 729, fontSize: 9 },
  payerAddress: { x: 60, y: 716, fontSize: 9 },
  payerCityStateZip: { x: 60, y: 704, fontSize: 9 },
  payerPhone: { x: 60, y: 690, fontSize: 8 },
  payerTIN: { x: 60, y: 665, fontSize: 10 },
  recipientTIN: { x: 185, y: 665, fontSize: 10 },
  recipientName: { x: 60, y: 640, fontSize: 10 },
  recipientAddress: { x: 60, y: 605, fontSize: 9 },
  recipientCityStateZip: { x: 60, y: 581, fontSize: 9 },
  accountNumber: { x: 60, y: 558, fontSize: 9 },
  secondTINNotice: { x: 265, y: 672, fontSize: 10 },
  box1: { x: 310, y: 665, fontSize: 10 },
  box2: { x: 485, y: 645, fontSize: 10 },
  box4: { x: 310, y: 593, fontSize: 10 },
  state1: { x: 310, y: 574, fontSize: 9 },
  payerStateNo1: { x: 390, y: 570, fontSize: 8 },
  stateTaxWithheld1: { x: 310, y: 570, fontSize: 9 },
  stateIncome1: { x: 510, y: 570, fontSize: 9 },
  state2: { x: 310, y: 570, fontSize: 9 },
  payerStateNo2: { x: 390, y: 555, fontSize: 8 },
  stateTaxWithheld2: { x: 310, y: 555, fontSize: 9 },
  stateIncome2: { x: 510, y: 555, fontSize: 9 }
};

// Generate 1099-NEC PDF
export const generate1099NECPDF = async (formData, taxYear) => {
  try {
    // Fetch the PDF template, honouring admin-tuned layouts + imported years
    const layout = await resolveTaxLayout("1099-nec", taxYear, DEFAULT_FIELD_POSITIONS, getTemplateUrl(taxYear));
    const FIELD_POSITIONS = layout.positions;
    const templateResponse = await fetch(layout.templateUrl);
    const templateBytes = await templateResponse.arrayBuffer();
    
    // Load the source PDF template
    const sourcePdf = await PDFDocument.load(templateBytes, { ignoreEncryption: true });
    
    // Create a new PDF with only page 3 (index 2) - Copy 1 for State Tax Department
    const pdfDoc = await PDFDocument.create();
    const [copiedPage] = await pdfDoc.copyPages(sourcePdf, [2]);
    pdfDoc.addPage(copiedPage);
    const page = pdfDoc.getPages()[0];
    
    console.log("PDF Generator: Created single-page PDF");
    
    // Get fonts
    const font = await pdfDoc.embedFont(StandardFonts.Courier);
    const boldFont = await pdfDoc.embedFont(StandardFonts.CourierBold);
    
    // Helper function to draw text
    const drawText = (text, position, useBold = false) => {
      if (!text) return;
      const textStr = String(text);
      page.drawText(textStr, {
        x: position.x,
        y: position.y,
        size: position.fontSize || 10,
        font: useBold ? boldFont : font,
        color: rgb(0, 0, 0),
      });
    };
    
    // Fill in the form fields
    
    // Payer Information
    drawText(formData.payerName, FIELD_POSITIONS.payerName, true);
    drawText(formData.payerAddress, FIELD_POSITIONS.payerAddress);
    const payerCityStateZip = [formData.payerCity, formData.payerState, formData.payerZip].filter(Boolean).join(", ");
    drawText(payerCityStateZip, FIELD_POSITIONS.payerCityStateZip);
    drawText(formData.payerPhone, FIELD_POSITIONS.payerPhone);
    drawText(formData.payerTIN, FIELD_POSITIONS.payerTIN);
    
    // Recipient Information
    drawText(formData.recipientTIN, FIELD_POSITIONS.recipientTIN);
    drawText(formData.recipientName, FIELD_POSITIONS.recipientName, true);
    drawText(formData.recipientAddress, FIELD_POSITIONS.recipientAddress);
    const recipientCityStateZip = [formData.recipientCity, formData.recipientState, formData.recipientZip].filter(Boolean).join(", ");
    drawText(recipientCityStateZip, FIELD_POSITIONS.recipientCityStateZip);
    drawText(formData.accountNumber, FIELD_POSITIONS.accountNumber);
    
    // Checkboxes
    if (formData.secondTINNotice) {
      drawText("X", FIELD_POSITIONS.secondTINNotice, true);
    }
    
    // Compensation boxes
    drawText(formatCurrency(formData.box1), FIELD_POSITIONS.box1);
    if (formData.box2) {
      drawText("X", FIELD_POSITIONS.box2, true);
    }
    drawText(formatCurrency(formData.box4), FIELD_POSITIONS.box4);
    
    // State Information - Row 1
    drawText(formData.state1, FIELD_POSITIONS.state1);
    drawText(formData.payerStateNo1, FIELD_POSITIONS.payerStateNo1);
    drawText(formatCurrency(formData.stateIncome1), FIELD_POSITIONS.stateIncome1);
    drawText(formatCurrency(formData.stateTaxWithheld1), FIELD_POSITIONS.stateTaxWithheld1);
    
    // State Information - Row 2
    drawText(formData.state2, FIELD_POSITIONS.state2);
    drawText(formData.payerStateNo2, FIELD_POSITIONS.payerStateNo2);
    drawText(formatCurrency(formData.stateIncome2), FIELD_POSITIONS.stateIncome2);
    drawText(formatCurrency(formData.stateTaxWithheld2), FIELD_POSITIONS.stateTaxWithheld2);
    
    // Save the PDF
    const pdfBytes = await pdfDoc.save();
    return pdfBytes;
    
  } catch (error) {
    console.error("Error generating 1099-NEC PDF:", error);
    throw error;
  }
};

// Generate and download 1099-NEC
export const generateAndDownload1099NEC = async (formData, taxYear, returnBlob = false) => {
  try {
    const pdfBytes = await generate1099NECPDF(formData, taxYear);
    
    // Create blob and download
    const blob = new Blob([pdfBytes], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const pdfFileName = `1099NEC_${taxYear}_${formData.recipientName?.replace(/\s+/g, '_') || 'Form'}.pdf`;
    
    // Store download info for payment success page
    sessionStorage.setItem('lastDownloadUrl', url);
    sessionStorage.setItem('lastDownloadFileName', pdfFileName);
    
    // Only when the caller is not taking the blob: an archived download plays
    // the success animation out first and then delivers the file itself (a new
    // tab on phone browsers), so downloading here as well would double it up.
    if (!returnBlob) {
      const link = document.createElement("a");
      link.href = url;
      link.download = pdfFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    
    // Don't revoke URL immediately - needed for re-download on success page
    if (returnBlob) {
      return blob;
    }
    return true;
  } catch (error) {
    console.error("Error downloading 1099-NEC:", error);
    throw error;
  }
};
